import React, { useState, useEffect, useRef } from 'react';
import { PlusCircle, X, Users, Pencil, Trash2, Check, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import BackButton from '../components/BackButton';
import BBALoader from '../components/BBALoader';
import { 
  saveOrgChartData, 
  loadOrgChartData, 
  addOrgChartEmployee, 
  updateOrgChartEmployee, 
  deleteOrgChartEmployee 
} from '../services/orgChartService';
import { getOrgChartTemplate, getUserRevenue } from '../services/orgChartTemplateService';
import { getCompanyIdForUser, findAccountIdByEmail, getPrimaryUserIdForAccount } from '../services/firestoreService';
import PortalBackground from '../components/PortalBackground';

// Function to get color based on grade
const getGradeColor = (grade) => {
  // A+ to A- are green shades
  if (grade === 'A+') return 'bg-green-500 text-white';
  if (grade === 'A') return 'bg-green-400 text-white';
  if (grade === 'A-') return 'bg-green-300 text-green-800';
  
  // B+ to B- are light green to yellow shades
  if (grade === 'B+') return 'bg-green-200 text-green-800';
  if (grade === 'B') return 'bg-yellow-200 text-yellow-800';
  if (grade === 'B-') return 'bg-yellow-300 text-yellow-800';
  
  // C+ to C- are yellow to orange shades
  if (grade === 'C+') return 'bg-yellow-400 text-yellow-800';
  if (grade === 'C') return 'bg-yellow-500 text-yellow-800';
  if (grade === 'C-') return 'bg-orange-300 text-orange-800';
  
  // D+ to D- are orange to red-orange shades
  if (grade === 'D+') return 'bg-orange-400 text-white';
  if (grade === 'D') return 'bg-orange-500 text-white';
  if (grade === 'D-') return 'bg-orange-600 text-white';
  
  // F is red
  if (grade === 'F') return 'bg-red-500 text-white';
  
  // Default
  return 'bg-gray-200 text-gray-800';
};

// Add these helper functions at the top with getGradeColor
const getTenureColor = (tenure) => {
  const years = parseFloat(tenure);
  if (years <= 0.5) return 'bg-red-100 text-red-800'; // Under 6 months
  if (years <= 1) return 'bg-yellow-100 text-yellow-800'; // Under 1 year
  if (years <= 3) return 'bg-green-100 text-green-800'; // Under 3 years
  if (years <= 5) return 'bg-green-300 text-green-800'; // Under 5 years
  return 'bg-green-500 text-white'; // 5+ years
};

const getSalaryColor = (salary) => {
  const amount = parseFloat(salary.replace(/[^0-9.-]+/g, ""));
  if (amount >= 100000) return 'bg-red-100 text-red-800';
  if (amount >= 75000) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
};

// overrideCompanyId lets an internal viewer (CRM Portal View) load a specific
// client account's org chart instead of resolving from the logged-in user.
const OrgChart = ({ embedded = false, overrideCompanyId = null }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [companyId, setCompanyId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [currentStep, setCurrentStep] = useState(1);
  const [isChartFinalized, setIsChartFinalized] = useState(false);
  const [finalizedEmployees, setFinalizedEmployees] = useState(null);
  const [proposedEmployees, setProposedEmployees] = useState(null);
  const [totalRevenue, setTotalRevenue] = useState(1000000); // Default $1M revenue
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // 1099 modal state
  const [is1099ModalOpen, setIs1099ModalOpen] = useState(false);
  const [selected1099Contractors, setSelected1099Contractors] = useState([]);
  const [editing1099Contractor, setEditing1099Contractor] = useState(null);
  const [editing1099Value, setEditing1099Value] = useState('');
  const [showEditIcons1099, setShowEditIcons1099] = useState(false);

  // Revenue input modal state
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const [revenueInput, setRevenueInput] = useState('');

  // Hiring Outline state (for future members)
  const [isHiringOutlineOpen, setIsHiringOutlineOpen] = useState(false);
  const [isHiringOutlineEditing, setIsHiringOutlineEditing] = useState(false);
  const [hiringOrder, setHiringOrder] = useState([]);
  const [hiredAtMap, setHiredAtMap] = useState({});

  // Predefined 1099 contractor options
  const predefined1099Options = ['Marketing', 'Designer', 'Draftsman', 'Bookkeeper'];

  // Template and analysis state
  const [templateData, setTemplateData] = useState(null);
  const [userRevenue, setUserRevenue] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [customRevenue, setCustomRevenue] = useState('');
  const [suggestedOrgChart, setSuggestedOrgChart] = useState(null);
  
  // Unassigned members and assignment state
  const [unassignedMembers, setUnassignedMembers] = useState([]);
  const [memberAssignments, setMemberAssignments] = useState({});
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [selectedUnassignedMember, setSelectedUnassignedMember] = useState(null);

  // Resolve company id so org chart is company-wide (all members see same data)
  useEffect(() => {
    if (overrideCompanyId) {
      setCompanyId(overrideCompanyId);
      return;
    }
    if (!currentUser?.uid) {
      setCompanyId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let cid = await getCompanyIdForUser(currentUser.uid);
        const fromUserDoc = !!cid;
        if (!cid && currentUser.email) cid = await findAccountIdByEmail(currentUser.email, 'Clients');
        if (!cancelled) {
          setCompanyId(cid || null);
          console.log('[OrgChart] companyId resolved', {
            userId: currentUser.uid,
            email: currentUser.email,
            fromUserDoc,
            companyId: cid || null
          });
        }
      } catch (err) {
        console.warn('[OrgChart] companyId resolution failed', err);
        if (!cancelled) setCompanyId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?.email, overrideCompanyId]);

  // For saves: use current companyId or re-resolve once so team member edits always go to company account
  const getCompanyIdForSave = async () => {
    if (overrideCompanyId) return overrideCompanyId;
    if (companyId) return companyId;
    if (!currentUser?.uid) return null;
    try {
      let cid = await getCompanyIdForUser(currentUser.uid);
      if (!cid && currentUser.email) cid = await findAccountIdByEmail(currentUser.email, 'Clients');
      if (cid) setCompanyId(cid);
      return cid || null;
    } catch {
      return null;
    }
  };

  // Function to save total revenue changes (always save to company account when user belongs to one)
  const saveTotalRevenue = async (newRevenue) => {
    try {
      const cid = await getCompanyIdForSave();
      await saveOrgChartData({
        employees,
        totalRevenue: newRevenue,
        isChartFinalized,
        finalizedEmployees,
        proposedEmployees,
        contractors1099: selected1099Contractors,
        hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
      }, cid);
    } catch (error) {
      console.error('Error saving total revenue:', error);
    }
  };
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    title: '',
    reportsTo: '',
    memberStatus: 'existing',
    grade: '',
    tenureYears: '0',
    salary: '50000',
    headerColor: 'bg-red-500'
  });

  // Details popup - shows when clicking a member (before edit)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedEmployeeForDetails, setSelectedEmployeeForDetails] = useState(null);

  // Constants for layout calculations - base sizes that will be affected by zoom
  const cardWidth = 240; // Width of each card in pixels (w-60 = 15rem = 240px)
  const spacing = 64;    // Space between cards in pixels (space-x-16 = 4rem = 64px)

  // Calculate actual sizes based on zoom level
  const getZoomedSize = (size) => (size * zoomLevel) / 100;

  const grades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;
      try {
        setLoading(true);
        console.log('[OrgChart] loading data', { companyId, userId: currentUser.uid });
        const orgChartData = await loadOrgChartData(companyId);
        const employeeCount = (orgChartData.employees || []).length;
        console.log('[OrgChart] loaded', { companyId, employeeCount });
        setEmployees(orgChartData.employees || []);
        setTotalRevenue(orgChartData.totalRevenue || 1000000);
        setIsChartFinalized(orgChartData.isChartFinalized || false);
        setFinalizedEmployees(orgChartData.finalizedEmployees || null);
        setProposedEmployees(orgChartData.proposedEmployees || null);
        setSelected1099Contractors(orgChartData.contractors1099 || []);
        setHiringOrder(orgChartData.hiringOutline?.order || []);
        setHiredAtMap(orgChartData.hiringOutline?.hiredAt || {});
      } catch (error) {
        console.error('[OrgChart] Error loading org chart data:', error);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentUser, companyId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tenureYears' || name === 'salary') {
      // Ensure non-negative numbers
      const numValue = Math.max(0, parseInt(value) || 0);
      setFormData(prev => ({
        ...prev,
        [name]: numValue.toString(),
        ...(name === 'tenureYears' && { tenure: numValue.toString() })
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleMemberStatusSelect = (status) => {
    setFormData(prev => ({
      ...prev,
      memberStatus: status,
      ...(status === 'future' && { name: 'Future Hire' })
    }));
  };



  const saveEmployee = async () => {
    // Validate required fields
    if (!formData.name || !formData.title || !formData.salary) {
      return;
    }
    
    // Special case: first employee, editing top-level employee, or Owner (owners don't report to anyone)
    const isFirstEmployee = employees.length === 0;
    const isTopLevelEmployee = formData.id && !employees.find(emp => emp.id === formData.id)?.reportsTo;
    const isOwner = formData.title?.toLowerCase() === 'owner';
    const reportsToNotRequired = isFirstEmployee || isTopLevelEmployee || isOwner;
    
    // Reports to is required unless owner, first employee, or editing existing top-level
    if (!reportsToNotRequired && !formData.reportsTo) {
      alert("Please select who this member reports to.");
      return;
    }
    
    try {
      setSaving(true);
      
      const newEmployee = {
        ...formData,
        id: formData.id || Date.now().toString(),
        // Owner, first employee, or top-level employee have no manager
        reportsTo: reportsToNotRequired ? null : formData.reportsTo,
        memberStatus: formData.memberStatus || 'existing',
        tenure: (parseFloat(formData.tenureYears) || 0).toString()
      };
      
      const cid = await getCompanyIdForSave();
      if (formData.id) {
        // Update existing employee
        await updateOrgChartEmployee(formData.id, newEmployee, cid);
        const updatedEmployees = employees.map(emp => 
          emp.id === formData.id ? newEmployee : emp
        );
        setEmployees(updatedEmployees);
        
        // Auto-save the complete org chart data to company account
        await saveOrgChartData({
          employees: updatedEmployees,
          totalRevenue,
          isChartFinalized,
          finalizedEmployees,
          proposedEmployees,
          contractors1099: selected1099Contractors,
          hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
        }, cid);
      } else {
        // Add new employee
        await addOrgChartEmployee(newEmployee, cid);
        const updatedEmployees = [...employees, newEmployee];
        setEmployees(updatedEmployees);
        
        // Auto-save the complete org chart data to company account
        await saveOrgChartData({
          employees: updatedEmployees,
          totalRevenue,
          isChartFinalized,
          finalizedEmployees,
          proposedEmployees,
          contractors1099: selected1099Contractors,
          hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
        }, cid);
      }
      
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving employee:', error);
      setMessage({ type: 'error', text: 'Failed to save employee. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      title: '',
      reportsTo: '',
      memberStatus: 'existing',
      grade: '',
      tenureYears: '0',
      salary: '50000',
      headerColor: 'bg-red-500'
    });
  };

  const editEmployee = (employee) => {
    setSelectedEmployeeForDetails(employee);
    setIsDetailsModalOpen(true);
  };

  const openEditFromDetails = () => {
    const employee = selectedEmployeeForDetails;
    if (!employee) return;
    const tenureYears = Math.round(parseFloat(employee.tenure) || 0);

    setFormData({
      ...employee,
      tenureYears: tenureYears.toString(),
      salary: employee.salary.replace(/[^0-9.-]+/g, ""),
      memberStatus: employee.memberStatus || 'existing'
    });
    setIsDetailsModalOpen(false);
    setSelectedEmployeeForDetails(null);
    setIsModalOpen(true);
  };

  const deleteEmployee = async (id) => {
    try {
      setSaving(true);
      const cid = await getCompanyIdForSave();
      await deleteOrgChartEmployee(id, cid);
      
      // Update local state
      const updatedEmployees = employees.filter(emp => emp.id !== id);
      setEmployees(updatedEmployees);
      
      // Auto-save to company account so all team members see the change
      await saveOrgChartData({
        employees: updatedEmployees,
        totalRevenue,
        isChartFinalized,
        finalizedEmployees,
        proposedEmployees,
        contractors1099: selected1099Contractors,
        hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
      }, cid);
    } catch (error) {
      console.error('Error deleting employee:', error);
      setMessage({ type: 'error', text: 'Failed to delete employee. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const getDirectReports = (managerId) => {
    return employees.filter(emp => emp.reportsTo === managerId);
  };

  // Add these new functions for drag functionality
  const handleMouseDown = (e) => {
    // Only start dragging if not clicking on a card or button
    if (e.target.closest('.employee-card') || e.target.closest('button')) {
      return;
    }
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for drag functionality
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Add recenter function
  const handleRecenter = () => {
    setPosition({ x: 0, y: 0 });
  };

  // Chart viewport refs (main chart + suggested chart in the analysis view)
  const chartViewportRef = useRef(null);
  const suggestedViewportRef = useRef(null);

  // Mouse-wheel zoom — native listener (passive: false) so we can block page scroll
  useEffect(() => {
    const els = [chartViewportRef.current, suggestedViewportRef.current].filter(Boolean);
    if (els.length === 0) return;
    const onWheel = (e) => {
      e.preventDefault();
      setZoomLevel(prev => Math.min(150, Math.max(30, Math.round(prev * (e.deltaY < 0 ? 1.06 : 0.94)))));
    };
    els.forEach(el => el.addEventListener('wheel', onWheel, { passive: false }));
    return () => els.forEach(el => el.removeEventListener('wheel', onWheel));
  }, [currentStep, loading, analysisData, suggestedOrgChart]);

  // Size the main chart viewport to the remaining window height so the page itself never scrolls.
  // Standalone runs to the window bottom (24px gap); embedded contexts (portal / CRM portal view)
  // have wrapper margins + layout padding below the chart, so reserve extra clearance there to
  // keep the last row from being clipped.
  useEffect(() => {
    const el = chartViewportRef.current;
    if (!el) return;
    const bottomGap = embedded ? 64 : 24;
    const sizeToViewport = () => {
      const top = el.getBoundingClientRect().top;
      el.style.height = `${Math.max(320, window.innerHeight - top - bottomGap)}px`;
    };
    sizeToViewport();
    window.addEventListener('resize', sizeToViewport);
    return () => window.removeEventListener('resize', sizeToViewport);
  }, [currentStep, loading, embedded]);

  // 1099 contractor functions
  const handle1099ContractorToggle = (contractor) => {
    setSelected1099Contractors(prev => {
      if (prev.includes(contractor)) {
        return prev.filter(c => c !== contractor);
      } else {
        return [...prev, contractor];
      }
    });
  };

  const handleRemove1099Contractor = (contractor) => {
    setSelected1099Contractors(prev => prev.filter(c => c !== contractor));
  };

  const handleSaveEdit1099 = () => {
    const newName = editing1099Value.trim();
    if (editing1099Contractor === '__new__') {
      if (newName && !selected1099Contractors.includes(newName) && !predefined1099Options.includes(newName)) {
        setSelected1099Contractors(prev => [...prev, newName]);
      }
    } else if (editing1099Contractor && newName) {
      const isDuplicate = selected1099Contractors.some(c => c !== editing1099Contractor && c === newName);
      if (!isDuplicate) {
        setSelected1099Contractors(prev => 
          prev.map(c => c === editing1099Contractor ? newName : c)
        );
      }
    }
    setEditing1099Contractor(null);
    setEditing1099Value('');
  };

  const handleCancelEdit1099 = () => {
    setEditing1099Contractor(null);
    setEditing1099Value('');
  };

  const handleAdd1099Click = () => {
    setEditing1099Contractor('__new__');
    setEditing1099Value('');
  };

  const handleSave1099Contractors = async () => {
    try {
      setSaving(true);
      const cid = await getCompanyIdForSave();
      await saveOrgChartData({
        employees,
        totalRevenue,
        isChartFinalized,
        finalizedEmployees,
        proposedEmployees,
        contractors1099: selected1099Contractors,
        hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
      }, cid);
      
      setIs1099ModalOpen(false);
    } catch (error) {
      console.error('Error saving 1099 contractors:', error);
      setMessage({ type: 'error', text: 'Failed to save 1099 contractors. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Analysis functions (use company primary user's revenue when company-scoped)
  const performAnalysis = async (customRevenueAmount = null) => {
    try {
      setLoading(true);
      const revenueUserId = companyId ? (await getPrimaryUserIdForAccount(companyId)) || currentUser.uid : currentUser.uid;
      const [template, revenue] = await Promise.all([
        getOrgChartTemplate(),
        getUserRevenue(revenueUserId)
      ]);
      
      setTemplateData(template);
      setUserRevenue(revenue);
      
      // Use custom revenue if provided, otherwise use user's revenue
      const revenueToAnalyze = customRevenueAmount || revenue;
      
      if (!revenueToAnalyze) {
        setMessage({ type: 'error', text: 'Please set your revenue in the Sales portal first or enter a custom revenue amount.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        return;
      }
      
      // Perform analysis
      const analysis = await generateAnalysis(template, revenueToAnalyze);
      setAnalysisData(analysis);
      
    } catch (error) {
      console.error('Error performing analysis:', error);
      setMessage({ type: 'error', text: 'Failed to perform analysis. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomRevenueAnalysis = () => {
    const revenue = parseFloat(customRevenue);
    if (isNaN(revenue) || revenue <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid revenue amount.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    performAnalysis(revenue);
  };

  const handleRevenueInputAnalysis = async () => {
    const revenue = parseFloat(revenueInput);
    if (isNaN(revenue) || revenue <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid revenue amount.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    
    setIsRevenueModalOpen(false);
    await performAnalysis(revenue);
    setCurrentStep(2);
  };

  // Member assignment functions
  const handleAssignMember = (memberId, suggestedPositionId) => {
    setMemberAssignments(prev => ({
      ...prev,
      [memberId]: suggestedPositionId
    }));
  };

  const handleRemoveAssignment = (memberId) => {
    setMemberAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[memberId];
      return newAssignments;
    });
  };

  const openAssignmentModal = (member) => {
    setSelectedUnassignedMember(member);
    setIsAssignmentModalOpen(true);
  };

  const generateAnalysis = async (template, revenue) => {
    // Get current org chart data
    const currentEmployees = finalizedEmployees || employees;
    const currentHeadcount = currentEmployees.length;
    
    // Determine proposed headcount based on template - only count actual positions (not title/reportsTo changes)
    const proposedPositions = template.positions.filter(pos => 
      pos.revenueThreshold <= revenue && pos.title && pos.title.trim() !== '' && 
      pos.changeType !== 'titleChange' && pos.changeType !== 'reportsToChange'
    );
    const proposedHeadcount = proposedPositions.length;
    
    console.log('Template positions:', template.positions);
    console.log('Proposed positions:', proposedPositions);
    console.log('Proposed positions with titles:', proposedPositions.filter(pos => pos.title && pos.title.trim() !== ''));
    
    // Generate suggested org chart - first pass: create employees with title-based reportsTo
    const suggestedEmployees = proposedPositions
      .filter(pos => pos.title && pos.title.trim() !== '') // Only process positions with valid titles
      .map((pos, index) => {
        // For suggested employees, we need to handle the reportsTo field properly
        // If reportsTo is empty string, null, or undefined, it should be null
        let reportsTo = pos.reportsTo && pos.reportsTo.trim() !== '' ? pos.reportsTo : null;
        
        // Ensure we have at least one top-level employee (Owner or first position)
        if (index === 0 || (pos.title && pos.title.toLowerCase().includes('owner'))) {
          reportsTo = null;
        }
        
        // Try to find existing employee with this title
        const existingEmployee = currentEmployees.find(emp => emp.title === pos.title);
        
        return {
          id: `suggested-${index}`,
          name: existingEmployee ? existingEmployee.name : pos.title,
          title: pos.title,
          reportsTo: reportsTo, // Keep as title for now
          grade: existingEmployee ? existingEmployee.grade : 'C',
          tenure: existingEmployee ? existingEmployee.tenure : '0',
          salary: existingEmployee ? existingEmployee.salary : '50000',
          headerColor: existingEmployee ? existingEmployee.headerColor : 'bg-blue-500',
          isSuggested: !existingEmployee
        };
      });
    
    // Apply reporting changes from template
    const allTemplatePositions = template.positions.filter(pos => pos.title && pos.title.trim() !== '');
    allTemplatePositions.forEach(templatePos => {
      if (templatePos.changeType === 'reportsToChange') {
        // Find the employee in suggested employees that matches this title
        const employeeToUpdate = suggestedEmployees.find(emp => emp.title === templatePos.title);
        if (employeeToUpdate && templatePos.reportsTo) {
          // Update the reportsTo field with the new manager title
          employeeToUpdate.reportsTo = templatePos.reportsTo;
        }
      }
    });
    
    // Second pass: convert title-based reportsTo to ID-based reportsTo and handle changes
    const suggestedEmployeesWithIds = suggestedEmployees.map(emp => {
      let reportsToId = null;
      if (emp.reportsTo) {
        const manager = suggestedEmployees.find(m => m.title === emp.reportsTo);
        if (manager) {
          reportsToId = manager.id;
        }
      }
      
      return {
        ...emp,
        reportsTo: reportsToId
      };
    });
    
    // Handle title changes and reportsTo changes - apply reporting changes to existing employees
    const finalSuggestedEmployees = suggestedEmployeesWithIds.filter(emp => {
      const position = proposedPositions.find(pos => pos.title === emp.title);
      if (!position) return true;
      
      // For reportsTo changes, update the employee's reportsTo field
      if (position.changeType === 'reportsToChange' && position.reportsTo) {
        const newManager = suggestedEmployeesWithIds.find(m => m.title === position.reportsTo);
        if (newManager) {
          emp.reportsTo = newManager.id;
        }
      }
      
      // Filter out title changes as they're not new positions to display
      return position.changeType !== 'titleChange';
    });
    
    console.log('Final suggested employees:', finalSuggestedEmployees);
    setSuggestedOrgChart(finalSuggestedEmployees);
    
    // Find unassigned members (current employees whose positions aren't in suggested org chart)
    const suggestedTitles = finalSuggestedEmployees.map(emp => emp.title);
    const unassigned = currentEmployees.filter(emp => !suggestedTitles.includes(emp.title));
    setUnassignedMembers(unassigned);
    
    // Generate scaling roadmap (missing positions ordered by revenue threshold)
    const missingPositions = template.positions
      .filter(pos => pos.title && pos.title.trim() !== '' && !suggestedTitles.includes(pos.title))
      .sort((a, b) => a.revenueThreshold - b.revenueThreshold);
    
    // Get suggested positions with revenue thresholds for the roadmap
    const suggestedPositionsWithThresholds = finalSuggestedEmployees.map(emp => {
      const templatePosition = template.positions.find(pos => pos.title === emp.title);
      return {
        ...emp,
        revenueThreshold: templatePosition ? templatePosition.revenueThreshold : 0
      };
    });
    
    // Calculate revenue per employee
    const revenuePerEmployee = currentHeadcount > 0 ? revenue / currentHeadcount : 0;
    
    // Calculate differences (current - proposed: negative means need to hire, positive means overstaffed)
    const headcountDifference = currentHeadcount - proposedHeadcount;
    
    // Determine staffing status based on headcount difference
    let staffingStatus = 'Unknown';
    let statusColor = 'gray';
    
    if (headcountDifference < 0) {
      staffingStatus = 'Need to Hire';
      statusColor = 'red';
    } else if (headcountDifference === 0) {
      staffingStatus = 'Solid';
      statusColor = 'green';
    } else {
      staffingStatus = 'Need to Optimize';
      statusColor = 'orange';
    }
    
    // Calculate grade metrics
    const grades = currentEmployees.map(emp => emp.grade);
    const gradeValues = { 'A+': 13, 'A': 12, 'A-': 11, 'B+': 10, 'B': 9, 'B-': 8, 'C+': 7, 'C': 6, 'C-': 5, 'D+': 4, 'D': 3, 'D-': 2, 'F': 1 };
    const gradeToLetter = { 13: 'A+', 12: 'A', 11: 'A-', 10: 'B+', 9: 'B', 8: 'B-', 7: 'C+', 6: 'C', 5: 'C-', 4: 'D+', 3: 'D', 2: 'D-', 1: 'F' };
    
    const averageGradeValue = grades.length > 0 ? 
      grades.reduce((sum, grade) => sum + (gradeValues[grade] || 6), 0) / grades.length : 6;
    const averageGrade = gradeToLetter[Math.round(averageGradeValue)] || 'C';
    
    // Find highest and lowest grades
    const highestGradeValue = grades.length > 0 ? Math.max(...grades.map(g => gradeValues[g] || 6)) : 6;
    const lowestGradeValue = grades.length > 0 ? Math.min(...grades.map(g => gradeValues[g] || 6)) : 6;
    const highestGrade = gradeToLetter[highestGradeValue] || 'C';
    const lowestGrade = gradeToLetter[lowestGradeValue] || 'C';
    
    // Grade analysis
    let gradeAnalysis = 'Average';
    if (averageGradeValue >= 12) gradeAnalysis = 'Great'; // A+ or A
    else if (averageGradeValue >= 9) gradeAnalysis = 'Solid'; // B+ or B
    else if (averageGradeValue >= 6) gradeAnalysis = 'Average'; // C+ or C
    else if (averageGradeValue >= 3) gradeAnalysis = 'Poor'; // D+ or D
    else gradeAnalysis = 'Bad'; // F
    
    // Calculate aim for revenue using template revenue per employee data
    const { rightSized: templateRightSized } = template.revenuePerEmployee;
    const aimForRevenue = templateRightSized ? (templateRightSized.min + templateRightSized.max) / 2 : 200000; // Use middle of right-sized range
    
    // Analyze 1099 contractors
    const recommended1099s = template.contractors.filter(contractor => 
      revenue >= contractor.startRevenue && (contractor.endRevenue === 0 || revenue <= contractor.endRevenue)
    );
    const current1099s = selected1099Contractors;
    const missing1099s = recommended1099s.filter(rec => !current1099s.includes(rec.title));
    const extra1099s = current1099s.filter(current => !recommended1099s.find(rec => rec.title === current));
    
    return {
      currentHeadcount,
      proposedHeadcount,
      revenuePerEmployee,
      staffingStatus,
      statusColor,
      headcountDifference,
      recommended1099s: recommended1099s.map(c => c.title),
      current1099s,
      missing1099s,
      extra1099s,
      revenue,
      recommendedPositions: proposedPositions,
      unassignedMembers: unassigned,
      missingPositions,
      suggestedPositionsWithThresholds,
      averageGrade,
      highestGrade,
      lowestGrade,
      gradeAnalysis,
      aimForRevenue
    };
  };

  // Render employee card
  const EmployeeCard = ({ employee, futureMemberIndex }) => {
    const zoomedWidth = getZoomedSize(cardWidth);
    
    // Format tenure to show years
    const formatTenure = (tenure) => {
      const years = parseFloat(tenure);
      return `${years} ${years === 1 ? 'Year' : 'Years'}`;
    };

    // Format salary to show as currency
    const formatSalary = (salary) => {
      const amount = parseFloat(salary.replace(/[^0-9.-]+/g, ""));
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(amount);
    };
    
    return (
      <div 
        className={`employee-card rounded-lg shadow-md overflow-hidden border transition-shadow ${
          darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
        } ${employee.isSuggested ? '' : 'cursor-pointer hover:shadow-lg'}`}
        style={{ width: `${zoomedWidth}px`, fontSize: `${zoomLevel/100}rem` }}
        onClick={() => !employee.isSuggested && editEmployee(employee)}
      >          
        {/* Header with name and title */}
        <div className={`${employee.headerColor || 'bg-red-500'} w-full py-2 px-3 text-center text-white`}>
          <h3 className="font-medium">{employee.name}</h3>
          <p className="text-xs">{employee.title}</p>
        </div>
        
        {/* Card body - different for suggested vs actual employees */}
        {employee.isSuggested ? (
          // Simplified card for suggested positions
          <div className="p-3 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Suggested Position
            </div>
          </div>
        ) : (
          // Details view - grade, tenure, salary
          <div className="p-3 flex items-center">
            {/* Member status icon: green check for existing, number for future */}
            <div className="mr-3">
              <div 
                className={`rounded-full flex items-center justify-center ${
                  employee.memberStatus === 'future' 
                    ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                    : 'bg-green-600 text-white'
                }`}
                style={{ 
                  width: `${getZoomedSize(64)}px`, 
                  height: `${getZoomedSize(64)}px` 
                }}
              >
                {employee.memberStatus === 'future' ? (
                  <span className="font-bold" style={{ fontSize: `${getZoomedSize(20)}px` }}>
                    {futureMemberIndex ?? '?'}
                  </span>
                ) : (
                  <Check size={getZoomedSize(32)} strokeWidth={3} />
                )}
              </div>
            </div>
            
            {/* Employee details */}
            <div className="flex-1 space-y-2">
              {employee.grade && (
                <div className={`px-2 py-1 ${getGradeColor(employee.grade)} rounded text-sm w-full text-center`}>
                  Grade: {employee.grade}
                </div>
              )}
              <div className={`px-2 py-1 ${getTenureColor(employee.tenure)} rounded text-sm w-full text-center`}>
                {formatTenure(employee.tenure)}
              </div>
              <div className={`px-2 py-1 ${getSalaryColor(employee.salary)} rounded text-sm w-full text-center`}>
                {formatSalary(employee.salary)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Single direct report branch
  const SingleReportBranch = ({ employee, employees: chartEmployees = employees, futureMemberOrderMap = {} }) => {
    const subReports = chartEmployees.filter(emp => emp.reportsTo === employee.id);
    const branchWidth = getZoomedSize(calculateBranchWidth(employee.id, chartEmployees));
    
    return (
      <div className="flex flex-col items-center">
        <div 
          style={{
            width: '4px',
            height: `${getZoomedSize(20)}px`,
            backgroundColor: '#9CA3AF',
            margin: '0 auto'
          }}
        />
                  <div className="relative" style={{ 
            width: `${branchWidth}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{ width: `${getZoomedSize(cardWidth)}px` }}>
              <EmployeeCard employee={employee} futureMemberIndex={futureMemberOrderMap[employee.id]} />
            </div>
            <NestedReports employeeId={employee.id} employees={chartEmployees} futureMemberOrderMap={futureMemberOrderMap} />
          </div>
      </div>
    );
  };

  // Helper function to calculate required width for a branch
  const calculateBranchWidth = (employeeId, chartEmployees = employees) => {
    const reports = chartEmployees.filter(emp => emp.reportsTo === employeeId);
    if (reports.length === 0) return cardWidth;
    if (reports.length === 1) {
      const subWidth = calculateBranchWidth(reports[0].id, chartEmployees);
      return Math.max(cardWidth, subWidth);
    }
    
    // Calculate width needed for each report's branch
    const reportWidths = reports.map(report => {
      const subReports = chartEmployees.filter(emp => emp.reportsTo === report.id);
      if (subReports.length === 0) return cardWidth;
      
      // Calculate width for children while maintaining parent's position
      const subWidth = calculateBranchWidth(report.id, chartEmployees);
      return Math.max(cardWidth, subWidth);
    });
    
    // Total width needed is sum of all report widths plus spacing between them
    return reportWidths.reduce((sum, width) => sum + width, 0) + 
           ((reports.length - 1) * spacing);
  };

  // Multiple direct reports branch with dynamic spacing
  const MultipleReportsBranch = ({ reports, employees: chartEmployees = employees, futureMemberOrderMap = {} }) => {
    const zoomedSpacing = getZoomedSize(spacing);
    const zoomedCardWidth = getZoomedSize(cardWidth);
    
    // Calculate branch widths for immediate children
    const childBranchWidths = reports.map(report => {
      const subReports = chartEmployees.filter(emp => emp.reportsTo === report.id);
      return getZoomedSize(
        subReports.length > 0 
          ? calculateBranchWidth(report.id, chartEmployees)
          : cardWidth
      );
    });
    
    // Calculate width for current row based on actual branch widths
    const currentRowWidth = childBranchWidths.reduce((sum, width) => sum + width, 0) + 
                          (zoomedSpacing * (reports.length - 1));
    
    return (
      <div className="flex flex-col items-center">
        {/* Vertical line from parent to horizontal connector */}
        <div 
          style={{
            width: '4px',
            height: `${getZoomedSize(20)}px`,
            backgroundColor: '#9CA3AF',
            margin: '0 auto'
          }}
        />
        
        {/* Container for this level */}
        <div className="relative" style={{ width: `${currentRowWidth}px` }}>
          {/* Horizontal connector line - only show if more than one report */}
          {reports.length > 1 && (() => {
            // Calculate the center positions of the first and last children
            let currentPosition = 0;
            const firstChildCenter = currentPosition + (childBranchWidths[0] / 2);
            
            currentPosition += childBranchWidths[0] + zoomedSpacing;
            for (let i = 1; i < childBranchWidths.length - 1; i++) {
              currentPosition += childBranchWidths[i] + zoomedSpacing;
            }
            const lastChildCenter = currentPosition + (childBranchWidths[childBranchWidths.length - 1] / 2);
            
            const lineWidth = lastChildCenter - firstChildCenter;
            const lineLeft = firstChildCenter;
            
            return (
              <div 
                className="absolute" 
                style={{
                  top: '0px',
                  left: `${lineLeft}px`,
                  width: `${lineWidth}px`,
                  height: '4px',
                  backgroundColor: '#9CA3AF',
                  zIndex: 1
                }}
              />
            );
          })()}
          
          {/* Reports row with dynamic spacing */}
          <div className="flex justify-center" style={{ gap: `${zoomedSpacing}px` }}>
            {reports.map((report, index) => {
              const subReports = chartEmployees.filter(emp => emp.reportsTo === report.id);
              const branchWidth = childBranchWidths[index];
              
              return (
                <div 
                  key={report.id} 
                  className="relative flex flex-col items-center"
                  style={{ 
                    width: `${branchWidth}px`
                  }}
                >
                  {/* Vertical line from horizontal connector to employee */}
                  <div 
                    style={{
                      width: '4px',
                      height: `${getZoomedSize(20)}px`,
                      backgroundColor: '#9CA3AF',
                      margin: '0 auto'
                    }}
                  />
                  <div className="relative flex flex-col items-center">
                    <div style={{ width: `${zoomedCardWidth}px` }}>
                      <EmployeeCard employee={report} futureMemberIndex={futureMemberOrderMap[report.id]} />
                    </div>
                    <NestedReports employeeId={report.id} employees={chartEmployees} futureMemberOrderMap={futureMemberOrderMap} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Component to handle direct reports display
  const NestedReports = ({ employeeId, employees: chartEmployees = employees, futureMemberOrderMap = {} }) => {
    const reports = chartEmployees.filter(emp => emp.reportsTo === employeeId);
    
    if (reports.length === 0) {
      return null;
    }
    
    if (reports.length === 1) {
      return <SingleReportBranch employee={reports[0]} employees={chartEmployees} futureMemberOrderMap={futureMemberOrderMap} />;
    }
    
    return <MultipleReportsBranch reports={reports} employees={chartEmployees} futureMemberOrderMap={futureMemberOrderMap} />;
  };

  // Helper to get future members in display order (uses hiringOrder if set, else tree traversal)
  const getFutureMemberOrderMap = (chartEmployees, customOrder = null) => {
    const futureIds = chartEmployees.filter(e => e.memberStatus === 'future' && !e.isSuggested).map(e => e.id);
    let order = [];
    if (customOrder && customOrder.length > 0) {
      order = customOrder.filter(id => futureIds.includes(id));
      futureIds.forEach(id => { if (!order.includes(id)) order.push(id); });
    } else {
      const traverse = (emp) => {
        if (emp.memberStatus === 'future' && !emp.isSuggested) order.push(emp.id);
        chartEmployees.filter(e => e.reportsTo === emp.id).forEach(r => traverse(r));
      };
      chartEmployees.filter(e => !e.reportsTo).forEach(emp => traverse(emp));
    }
    const map = {};
    order.forEach((id, i) => { map[id] = i + 1; });
    return map;
  };

  // Organization chart component
  const OrganizationChart = ({ employees: chartEmployees = employees }) => {
    const futureMemberOrderMap = getFutureMemberOrderMap(chartEmployees, hiringOrder);
    // Find all top-level employees (those with no manager)
    const topLevelEmployees = chartEmployees.filter(emp => !emp.reportsTo);
    
    if (topLevelEmployees.length === 0) {
      return (
        <div className={`text-center py-16 px-8 rounded-lg border border-dashed ${
          darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'
        }`}>
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No members added yet</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Add members to start building your organizational chart</p>
          <div className="flex justify-center">
            <AddButton
              onClick={() => setIsModalOpen(true)}
              className="!w-auto !h-auto px-4 py-2 rounded-lg"
            >
              <span className="ml-2">Add Your First Member</span>
            </AddButton>
          </div>
        </div>
      );
    }
    
    // For a single top-level employee
    if (topLevelEmployees.length === 1) {
      return (
        <div className="flex flex-col items-center pt-8">
          <div className="relative" style={{ width: '240px' }}>
            <EmployeeCard employee={topLevelEmployees[0]} futureMemberIndex={futureMemberOrderMap[topLevelEmployees[0].id]} />
            <NestedReports employeeId={topLevelEmployees[0].id} employees={chartEmployees} futureMemberOrderMap={futureMemberOrderMap} />
          </div>
        </div>
      );
    }
    
    // For multiple top-level employees (e.g., multiple owners)
    // Use same structure as MultipleReportsBranch: vertical lines, horizontal connector, then content
    const zoomedSpacing = getZoomedSize(spacing);
    const zoomedCardWidth = getZoomedSize(cardWidth);
    const childBranchWidths = topLevelEmployees.map(emp => {
      const subReports = chartEmployees.filter(e => e.reportsTo === emp.id);
      return getZoomedSize(subReports.length > 0 ? calculateBranchWidth(emp.id, chartEmployees) : cardWidth);
    });
    const currentRowWidth = childBranchWidths.reduce((sum, w) => sum + w, 0) + (zoomedSpacing * (topLevelEmployees.length - 1));

    return (
      <div className="flex flex-col items-center pt-8">
        <div className="relative" style={{ width: `${currentRowWidth}px` }}>
          {/* Horizontal connector line - spans from center of first to center of last owner */}
          {topLevelEmployees.length > 1 && (() => {
            let currentPosition = 0;
            const firstChildCenter = currentPosition + (childBranchWidths[0] / 2);
            currentPosition += childBranchWidths[0] + zoomedSpacing;
            for (let i = 1; i < childBranchWidths.length - 1; i++) {
              currentPosition += childBranchWidths[i] + zoomedSpacing;
            }
            const lastChildCenter = currentPosition + (childBranchWidths[childBranchWidths.length - 1] / 2);
            const lineWidth = lastChildCenter - firstChildCenter;
            const lineLeft = firstChildCenter;
            return (
              <div 
                className="absolute" 
                style={{
                  top: '0px',
                  left: `${lineLeft}px`,
                  width: `${lineWidth}px`,
                  height: '4px',
                  backgroundColor: '#9CA3AF',
                  zIndex: 1
                }}
              />
            );
          })()}
          
          {/* Owners row with vertical lines from horizontal connector to each owner */}
          <div className="flex justify-center" style={{ gap: `${zoomedSpacing}px` }}>
            {topLevelEmployees.map((employee, index) => {
              const branchWidth = childBranchWidths[index];
              return (
                <div 
                  key={employee.id} 
                  className="relative flex flex-col items-center"
                  style={{ width: `${branchWidth}px` }}
                >
                  {/* Vertical line from horizontal connector to owner */}
                  <div 
                    style={{
                      width: '4px',
                      height: `${getZoomedSize(20)}px`,
                      backgroundColor: '#9CA3AF',
                      margin: '0 auto'
                    }}
                  />
                  <div className="relative flex flex-col items-center">
                    <div style={{ width: `${zoomedCardWidth}px` }}>
                      <EmployeeCard employee={employee} futureMemberIndex={futureMemberOrderMap[employee.id]} />
                    </div>
                    <NestedReports employeeId={employee.id} employees={chartEmployees} futureMemberOrderMap={futureMemberOrderMap} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Ideal org structure based on revenue
  const getIdealOrgStructure = (revenue) => {
    const structures = {
      small: { // Under $2M
        roles: [
          { title: 'Owner', count: 1, minSalary: 150000 },
          { title: 'Project Manager', count: 1, minSalary: 80000 },
          { title: 'Designer', count: 1, minSalary: 65000 },
          { title: 'Carpenter', count: 2, minSalary: 55000 },
        ]
      },
      medium: { // $2M - $5M
        roles: [
          { title: 'Owner', count: 1, minSalary: 200000 },
          { title: 'Operations Director', count: 1, minSalary: 120000 },
          { title: 'Project Manager', count: 2, minSalary: 85000 },
          { title: 'Designer', count: 2, minSalary: 70000 },
          { title: 'Carpenter', count: 4, minSalary: 60000 },
          { title: 'Estimator', count: 1, minSalary: 75000 },
          { title: 'Client Services Coordinator', count: 1, minSalary: 55000 },
        ]
      },
      large: { // Over $5M
        roles: [
          { title: 'Owner', count: 1, minSalary: 250000 },
          { title: 'Operations Director', count: 1, minSalary: 150000 },
          { title: 'Project Director', count: 1, minSalary: 130000 },
          { title: 'Project Manager', count: 3, minSalary: 90000 },
          { title: 'Designer', count: 3, minSalary: 75000 },
          { title: 'Carpenter', count: 6, minSalary: 65000 },
          { title: 'Estimator', count: 2, minSalary: 80000 },
          { title: 'Client Services Coordinator', count: 2, minSalary: 60000 },
          { title: 'Marketing', count: 1, minSalary: 70000 },
          { title: 'Bookkeeper', count: 1, minSalary: 65000 },
        ]
      }
    };

    if (revenue < 2000000) return structures.small;
    if (revenue < 5000000) return structures.medium;
    return structures.large;
  };

  // Function to analyze differences between current and ideal structure
  const analyzeOrgStructure = () => {
    const idealStructure = getIdealOrgStructure(totalRevenue);
    const currentRoles = {};
    
    // Count current roles
    finalizedEmployees.forEach(emp => {
      currentRoles[emp.title] = (currentRoles[emp.title] || 0) + 1;
    });

    // Compare with ideal structure
    const analysis = {
      unfilled: [], // Missing roles
      overfilled: [], // Too many in role
      misaligned: [], // Salary misalignment
      total: {
        current: finalizedEmployees.length,
        ideal: idealStructure.roles.reduce((sum, role) => sum + role.count, 0),
        difference: 0
      }
    };

    idealStructure.roles.forEach(role => {
      const currentCount = currentRoles[role.title] || 0;
      const idealCount = role.count;

      // Check for unfilled positions
      if (currentCount < idealCount) {
        analysis.unfilled.push({
          title: role.title,
          missing: idealCount - currentCount,
          minSalary: role.minSalary
        });
      }

      // Check for overfilled positions
      if (currentCount > idealCount) {
        analysis.overfilled.push({
          title: role.title,
          excess: currentCount - idealCount
        });
      }

      // Check for salary misalignment
      const employeesInRole = finalizedEmployees.filter(emp => emp.title === role.title);
      employeesInRole.forEach(emp => {
        const salary = parseFloat(emp.salary.replace(/[^0-9.-]+/g, ""));
        if (salary < role.minSalary) {
          analysis.misaligned.push({
            name: emp.name,
            title: role.title,
            currentSalary: salary,
            recommendedSalary: role.minSalary,
            difference: role.minSalary - salary
          });
        }
      });
    });

    analysis.total.difference = analysis.total.ideal - analysis.total.current;
    return analysis;
  };

  // Function to generate proposed org chart
  const generateProposedChart = async () => {
    if (!finalizedEmployees) return;
    
    const analysis = analyzeOrgStructure();
    const idealStructure = getIdealOrgStructure(totalRevenue);
    
    // Start with existing employees
    let proposed = [...finalizedEmployees];

    // Add placeholder employees for unfilled positions
    analysis.unfilled.forEach(role => {
      for (let i = 0; i < role.missing; i++) {
        proposed.push({
          id: `proposed-${role.title}-${i}`,
          name: `[Proposed] ${role.title}`,
          title: role.title,
          salary: role.minSalary.toString(),
          grade: 'C',
          tenure: '0',
          headerColor: 'bg-green-500', // Green for proposed new positions
          isProposed: true
        });
      }
    });

    // Mark overfilled positions
    proposed = proposed.map(emp => {
      const isOverfilled = analysis.overfilled.find(role => role.title === emp.title);
      if (isOverfilled) {
        return {
          ...emp,
          headerColor: 'bg-red-500', // Red for overfilled positions
          isOverfilled: true
        };
      }
      return emp;
    });

    // Mark misaligned salaries
    proposed = proposed.map(emp => {
      const isMisaligned = analysis.misaligned.find(m => m.name === emp.name);
      if (isMisaligned) {
        return {
          ...emp,
          headerColor: 'bg-yellow-500', // Yellow for salary misalignment
          recommendedSalary: isMisaligned.recommendedSalary,
          isMisaligned: true
        };
      }
      return emp;
    });

    setProposedEmployees(proposed);
    
    // Save the proposed chart data to company account
    try {
      const cid = await getCompanyIdForSave();
      await saveOrgChartData({
        employees,
        totalRevenue,
        isChartFinalized,
        finalizedEmployees,
        proposedEmployees: proposed,
        contractors1099: selected1099Contractors,
        hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
      }, cid);
    } catch (error) {
      console.error('Error saving proposed chart:', error);
    }
    
    return analysis;
  };

  // Function to handle chart finalization
  const handleFinalizeChart = async () => {
    try {
      setSaving(true);
      
      setFinalizedEmployees([...employees]);
      setIsChartFinalized(true);
      
      const cid = await getCompanyIdForSave();
      await saveOrgChartData({
        employees,
        totalRevenue,
        isChartFinalized: true,
        finalizedEmployees: [...employees],
        proposedEmployees: null,
        contractors1099: selected1099Contractors,
        hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
      }, cid);
      
      // Generate proposed chart when finalizing
      setTimeout(() => {
        generateProposedChart();
      }, 0);
    } catch (error) {
      console.error('Error finalizing chart:', error);
      alert('Failed to finalize chart. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Function to navigate to proposed chart
  const handleSeeProposed = async () => {
    // Check if user has revenue set, if not show modal
    if (!userRevenue) {
      setIsRevenueModalOpen(true);
      return;
    }
    
    await performAnalysis();
    setCurrentStep(2);
  };

  // Function to render step content based on current step
  const renderStepContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-16">
          <BBALoader size={128} />
        </div>
      );
    }

    switch(currentStep) {
      case 1:
        return (
          <>
            <div className="flex justify-between items-center mb-6">
              <div className={`flex items-center gap-8 text-xl ${darkMode ? 'text-white' : 'text-black'}`}>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">Headcount:</span>
                  <span>{employees.length}</span>
                  {employees.filter(e => e.memberStatus === 'future').length > 0 && (
                    <button
                      onClick={() => {
                        const futureMembers = employees.filter(e => e.memberStatus === 'future');
                        if (hiringOrder.length === 0) {
                          const orderMap = getFutureMemberOrderMap(employees, null);
                          setHiringOrder(Object.entries(orderMap).sort((a, b) => a[1] - b[1]).map(([id]) => id));
                        }
                        setIsHiringOutlineEditing(false);
                        setIsHiringOutlineOpen(true);
                      }}
                      className="p-1 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors"
                      title="Hiring Outline"
                    >
                      <Plus size={18} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">Total Salary:</span>
                  <span>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0
                    }).format(
                      employees.reduce((total, emp) => 
                        total + parseFloat(emp.salary.replace(/[^0-9.-]+/g, "")), 0
                      )
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIs1099ModalOpen(true)}
                  className={`px-3 py-1 rounded flex items-center gap-2 ${
                    darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                  title="Manage 1099 Contractors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="m22 21-2-2"/>
                    <path d="M16 16h6"/>
                  </svg>
                  1099's
                </button>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={handleRecenter}
                  className={`px-3 py-1 rounded flex items-center gap-2 ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                  title="Recenter chart"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="16"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                  </svg>
                  Recenter
                </button>
                <AddButton
                  onClick={() => {
                    resetForm();
                    setIsModalOpen(true);
                  }}
                  className="!w-auto !h-auto px-4 py-2 rounded-lg"
                >
                  <span className="ml-2">Add Member</span>
                </AddButton>
              </div>
            </div>

            <div
              ref={chartViewportRef}
              className="w-full flex justify-center overflow-hidden cursor-grab active:cursor-grabbing select-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div
                className="transform-origin-top transition-transform duration-100 select-none"
                style={{
                  transform: `scale(${zoomLevel/100}) translate(${position.x}px, ${position.y}px)`,
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
              >
                <OrganizationChart />
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <div className="p-4">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <BBALoader size={128} />
              </div>
            ) : !analysisData ? (
              <div className={`p-8 text-center rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Set your revenue to run the analysis. Click the Analyze tab again after entering your revenue.
                </p>
                <button
                  onClick={() => handleSeeProposed()}
                  className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600'}`}
                >
                  Run Analysis
                </button>
              </div>
            ) : (
              <ProposedChart />
            )}
          </div>
        );
      case 3:
        return (
          <div className="p-4">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Summary</h2>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-900'}>Summary view coming soon...</p>
          </div>
        );
      default:
        return null;
    }
  };

  // Add ProposedChart component
  const ProposedChart = () => {
    if (!analysisData || !templateData) return null;

    const getStatusColor = (status, color) => {
      switch (color) {
        case 'red': return darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800';
        case 'green': return darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800';
        case 'orange': return darkMode ? 'bg-orange-900 text-orange-200' : 'bg-orange-100 text-orange-800';
        default: return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
      }
    };

    const getDifferenceColor = (difference) => {
      if (difference > 0) return 'text-red-600';
      if (difference < 0) return 'text-green-600';
      return 'text-gray-600';
    };
    
    return (
      <div className="w-full">


        <div className={`mb-6 p-6 rounded-lg shadow border ${
          darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-xl font-bold mb-6 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            ORG Chart Analysis
          </h3>
          
          {/* Row 1: Headcount Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Current Headcount */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  {analysisData.currentHeadcount}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Current Headcount
                </div>
              </div>
            </div>

            {/* Proposed Headcount */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  {analysisData.proposedHeadcount}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Proposed Headcount
                </div>
              </div>
            </div>

            {/* Headcount Difference */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-lg font-semibold ${getDifferenceColor(analysisData.headcountDifference)}`}>
                  {analysisData.headcountDifference}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Headcount Difference
                </div>
              </div>
            </div>

            {/* Analysis Status */}
            <div className={`p-4 rounded-lg border ${getStatusColor(analysisData.staffingStatus, analysisData.statusColor)}`}>
              <div className="text-center">
                <div className="text-lg font-semibold">
                  {analysisData.staffingStatus}
                </div>
                <div className="text-sm opacity-80">
                  Analysis
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Revenue Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Revenue */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  {formatCurrency(analysisData.revenue)}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Revenue
                </div>
              </div>
            </div>

            {/* Revenue Per Employee */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  {formatCurrency(analysisData.revenuePerEmployee)}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Revenue Per Employee
                </div>
              </div>
            </div>

            {/* Revenue/Employee Analysis */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-lg font-semibold ${getDifferenceColor(analysisData.headcountDifference)}`}>
                  {analysisData.revenuePerEmployee >= 200000 ? 'High' : analysisData.revenuePerEmployee >= 150000 ? 'Solid' : 'Low'}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Revenue/Employee Analysis
                </div>
              </div>
            </div>

            {/* Aim For */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(analysisData.aimForRevenue || 200000)}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Aim For
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Grade Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Highest Grade */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  {analysisData.highestGrade || 'C'}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Highest Grade
                </div>
              </div>
            </div>

            {/* Lowest Grade */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  {analysisData.lowestGrade || 'C'}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Lowest Grade
                </div>
              </div>
            </div>

            {/* Average Grade */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  {analysisData.averageGrade || 'C'}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Average Grade
                </div>
              </div>
            </div>

            {/* Grade Analysis */}
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {analysisData.gradeAnalysis || 'Solid'}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Grade Analysis
                </div>
              </div>
            </div>
          </div>

          {/* 1099s Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {['Marketing', 'Bookkeeper', 'Designer', 'Draftsman'].map((contractor) => {
              const isRecommended = analysisData.recommended1099s.includes(contractor);
              const hasContractor = analysisData.current1099s.includes(contractor);
              
              let cardColor = '';
              let status = '';
              
              if (isRecommended && hasContractor) {
                cardColor = darkMode ? 'bg-green-900 text-green-200 border-green-700' : 'bg-green-100 text-green-800 border-green-200';
                status = '✓';
              } else if (!isRecommended && hasContractor) {
                cardColor = darkMode ? 'bg-yellow-900 text-yellow-200 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
                status = '!';
              } else if (isRecommended && !hasContractor) {
                cardColor = darkMode ? 'bg-red-900 text-red-200 border-red-700' : 'bg-red-100 text-red-800 border-red-200';
                status = '!';
              } else {
                cardColor = darkMode ? 'border-gray-600 bg-gray-700 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600';
                status = '-';
              }
              
              return (
                <div key={contractor} className={`p-4 rounded-lg border ${cardColor}`}>
                  <div className="text-center">
                    <div className="text-lg font-semibold">
                      {contractor} {status}
                    </div>
                    <div className="text-sm opacity-80">
                      {isRecommended ? 'Recommended' : 'Not Needed'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>


        </div>

        {/* Unassigned Members Section */}
        {analysisData.unassignedMembers && analysisData.unassignedMembers.length > 0 && (
          <div className={`mb-6 p-6 rounded-lg shadow border ${
            darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Unassigned Members
            </h3>
            <p className={`text-sm mb-4 text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              These team members have positions that aren't in the suggested org chart. You can assign them to suggested positions or keep them as additional roles.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {analysisData.unassignedMembers.map((member) => {
                const assignedPositionId = memberAssignments[member.id];
                const assignedPosition = assignedPositionId ? 
                  analysisData.suggestedPositionsWithThresholds.find(pos => pos.id === assignedPositionId) : null;
                
                // Find revenue threshold for this position from template
                const templatePosition = analysisData.recommendedPositions.find(pos => pos.title === member.title);
                const revenueThreshold = templatePosition ? templatePosition.revenueThreshold : null;
                
                return (
                  <div key={member.id} className={`p-4 rounded-lg border ${
                    darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {member.name}
                      </h4>
                      <button
                        onClick={() => openAssignmentModal(member)}
                        className={`px-2 py-1 text-xs rounded ${
                          darkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        Assign
                      </button>
                    </div>
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {member.title}
                    </p>
                    {revenueThreshold && (
                      <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Typically hired at {formatCurrency(revenueThreshold)} revenue
                      </p>
                    )}
                    {assignedPosition && (
                      <div className={`text-xs p-2 rounded ${
                        darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                      }`}>
                        Assigned to: {assignedPosition.title}
                        <button
                          onClick={() => handleRemoveAssignment(member.id)}
                          className="ml-2 text-xs underline"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Suggested Org Chart */}
        {suggestedOrgChart && suggestedOrgChart.length > 0 && (
          <div className={`mb-6 p-6 rounded-lg shadow border ${
            darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Suggested Organization Chart for {formatCurrency(analysisData.revenue)} Revenue
              </h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleRecenter}
                  className={`px-3 py-1 rounded flex items-center gap-2 ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                  title="Recenter chart"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="16"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                  </svg>
                  Recenter
                </button>
              </div>
            </div>
            <div className="mb-4">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-900'}>Suggested Position</span>
                </div>
              </div>
            </div>
            
            <div
              ref={suggestedViewportRef}
              className="w-full flex justify-center overflow-hidden cursor-grab active:cursor-grabbing select-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div
                className="transform-origin-top transition-transform duration-100 select-none"
                style={{
                  transform: `scale(${zoomLevel/100}) translate(${position.x}px, ${position.y}px)`,
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
              >
                <OrganizationChart employees={suggestedOrgChart} />
              </div>
            </div>
          </div>
        )}

        {/* Suggested Positions & Revenue Thresholds */}
        {analysisData.suggestedPositionsWithThresholds?.filter(
          position => position.revenueThreshold <= analysisData.revenue && position.isSuggested
        ).length > 0 && (
          <div className={`mb-6 p-6 rounded-lg shadow border ${
            darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Suggested Positions & Revenue Thresholds
            </h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              These positions are recommended for your current revenue level. Each position shows when it's typically hired.
            </p>
            
            <div className="space-y-3">
              {analysisData.suggestedPositionsWithThresholds
                .filter(position => position.revenueThreshold <= analysisData.revenue && position.isSuggested)
                .sort((a, b) => a.revenueThreshold - b.revenueThreshold)
                .map((position, index) => (
                <div key={position.id} className={`p-4 rounded-lg border ${
                  darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {position.title}
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Hired at: {formatCurrency(position.revenueThreshold)} revenue
                        </p>
                      </div>
                    </div>
                    {position.name !== position.title && (
                      <div className={`text-sm px-2 py-1 rounded ${
                        darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                      }`}>
                        ✓ {position.name}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={embedded ? 'w-full' : 'relative w-full min-h-screen p-6'}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {/* Header section (hidden inside the Operations tab hub — the tab provides context) */}
      {!embedded && (
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/portal/operations')} />
          <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Organizational Chart</h1>
          <div className="w-[120px]"></div>
        </div>
      )}

      {/* Message display */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Chart content (Analyze tab removed — always shows the chart) */}
      {renderStepContent()}

      {/* Add/Edit Employee Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-xl max-h-screen overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h2 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                {formData.id ? 'Edit Member' : 'Add Member'}
              </h2>
              <button 
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  list="title-suggestions"
                  placeholder="Type or select a title"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  required
                />
                <datalist id="title-suggestions">
                  <option value="Owner" />
                  <option value="Operations Director" />
                  <option value="Director of Operations" />
                  <option value="Director of Construction" />
                  <option value="Director of Design" />
                  <option value="Project Director" />
                  <option value="Project Manager" />
                  <option value="Carpenter" />
                  <option value="Designer" />
                  <option value="Draftsman" />
                  <option value="Estimator" />
                  <option value="Client Services Coordinator" />
                  <option value="Marketing" />
                  <option value="Bookkeeper" />
                </datalist>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Reports To {formData.title?.toLowerCase() !== 'owner' && '*'}
                </label>
                <select
                  name="reportsTo"
                  value={formData.reportsTo || ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  required={formData.title?.toLowerCase() !== 'owner'}
                >
                  <option value="">{formData.title?.toLowerCase() === 'owner' ? 'None (Owner)' : 'Select Manager'}</option>
                  {employees
                    .filter(emp => emp.id !== formData.id)
                    .map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} - {emp.title}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Member Status</label>
                <div className="flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => handleMemberStatusSelect('existing')}
                    className={`flex-1 px-4 py-2 text-sm font-medium ${
                      (formData.memberStatus || 'existing') === 'existing'
                        ? 'bg-green-600 text-white'
                        : darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Existing Member
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMemberStatusSelect('future')}
                    className={`flex-1 px-4 py-2 text-sm font-medium ${
                      formData.memberStatus === 'future'
                        ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                        : darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Future Member
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Grade</label>
                      <select
                        name="grade"
                        value={formData.grade}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      >
                        <option value="">No Grade</option>
                        {grades.map(grade => (
                          <option key={grade} value={grade}>{grade}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tenure (Years)</label>
                      <input
                        type="number"
                        name="tenureYears"
                        value={formData.tenureYears}
                        onChange={handleInputChange}
                        min="0"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                    </div>

                    <div>
                      <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Salary</label>
                      <div className="flex items-center">
                        <span className={`mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                        <input
                          type="number"
                          name="salary"
                          value={formData.salary}
                          onChange={handleInputChange}
                          min="0"
                          step="1000"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Header Color</label>
                    <select
                      name="headerColor"
                      value={formData.headerColor}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    >
                      <option value="bg-red-500">Red</option>
                      <option value="bg-blue-500">Blue</option>
                      <option value="bg-green-500">Green</option>
                      <option value="bg-purple-500">Purple</option>
                      <option value="bg-indigo-500">Indigo</option>
                      <option value="bg-pink-500">Pink</option>
                      <option value="bg-yellow-500">Yellow</option>
                      <option value="bg-gray-500">Gray</option>
                    </select>
                  </div>
                </div>

              <div className="flex justify-between gap-3 pt-4">
                {formData.id ? (
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this member?')) {
                        deleteEmployee(formData.id);
                        setIsModalOpen(false);
                        resetForm();
                      }
                    }}
                    disabled={saving}
                    className={`px-4 py-2 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode ? 'bg-red-900 text-red-200 hover:bg-red-800' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {saving ? 'Deleting...' : 'Delete'}
                  </button>
                ) : (
                  <div />
                )}
                <button
                  onClick={() => saveEmployee()}
                  disabled={!formData.name || !formData.title || saving}
                  className={`px-4 py-2 rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                    !formData.name || !formData.title || saving
                      ? 'bg-green-300 dark:bg-green-900/50 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Details Modal */}
      {isDetailsModalOpen && selectedEmployeeForDetails && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h2 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                Member Details
              </h2>
              <button 
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedEmployeeForDetails(null);
                }}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>

            <div className={`space-y-3 mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <div>
                <span className="font-medium">Name:</span> {selectedEmployeeForDetails.name}
              </div>
              <div>
                <span className="font-medium">Title:</span> {selectedEmployeeForDetails.title}
              </div>
              <div>
                <span className="font-medium">Reports To:</span>{' '}
                {selectedEmployeeForDetails.reportsTo
                  ? employees.find(e => e.id === selectedEmployeeForDetails.reportsTo)?.name || '—'
                  : 'None'}
              </div>
              <div>
                <span className="font-medium">Member Status:</span>{' '}
                {selectedEmployeeForDetails.memberStatus === 'future' ? 'Future Member' : 'Existing Member'}
              </div>
              <div>
                <span className="font-medium">Grade:</span> {selectedEmployeeForDetails.grade || '—'}
              </div>
              <div>
                <span className="font-medium">Tenure:</span>{' '}
                {parseFloat(selectedEmployeeForDetails.tenure)} {parseFloat(selectedEmployeeForDetails.tenure) === 1 ? 'Year' : 'Years'}
              </div>
              <div>
                <span className="font-medium">Salary:</span>{' '}
                {selectedEmployeeForDetails.salary}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedEmployeeForDetails(null);
                }}
                className={`px-4 py-2 rounded-md ${
                  darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Close
              </button>
              <button
                onClick={openEditFromDetails}
                className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1099 Contractors Modal */}
      {is1099ModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-lg max-h-screen overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h2 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                1099 Contractors
              </h2>
              <button 
                onClick={() => {
                  setIs1099ModalOpen(false);
                  handleCancelEdit1099();
                  setShowEditIcons1099(false);
                }}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>

            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Click a card to select or deselect. Green = selected, grey = not selected.
            </p>

            {/* Cards grid - 2 per row */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Predefined contractor cards */}
              {predefined1099Options.map((option) => {
                const isSelected = selected1099Contractors.includes(option);
                return (
                  <div
                    key={option}
                    onClick={() => handle1099ContractorToggle(option)}
                    className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all min-h-[80px] min-w-0 flex items-center justify-center ${
                      isSelected
                        ? 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600'
                        : 'bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <span className={`font-medium text-center break-words overflow-hidden w-full ${isSelected ? 'text-green-800 dark:text-green-200' : 'text-gray-600 dark:text-gray-400'}`} title={option}>
                      {option}
                    </span>
                  </div>
                );
              })}
              {/* Custom contractor cards */}
              {selected1099Contractors
                .filter(c => !predefined1099Options.includes(c))
                .map((contractor) => (
                  <div
                    key={contractor}
                    onClick={(e) => !e.target.closest('button') && handle1099ContractorToggle(contractor)}
                    className={`relative p-4 rounded-lg border-2 min-h-[80px] min-w-0 flex flex-col items-center justify-center cursor-pointer transition-all ${
                      selected1099Contractors.includes(contractor)
                        ? 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600'
                        : 'bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <span className={`font-medium text-center break-words overflow-hidden w-full ${
                      selected1099Contractors.includes(contractor) ? 'text-green-800 dark:text-green-200' : 'text-gray-600 dark:text-gray-400'
                    }`} title={contractor}>
                      {contractor}
                    </span>
                    {showEditIcons1099 && (
                      <div className="flex items-center justify-between w-full mt-2 px-2 gap-4">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemove1099Contractor(contractor); }}
                          className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing1099Contractor(contractor);
                            setEditing1099Value(contractor);
                          }}
                          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              {/* New contractor card (editable, appears when Add is clicked) */}
              {editing1099Contractor === '__new__' && (
                <div
                  className="relative p-4 rounded-lg border-2 min-h-[80px] flex items-center justify-between gap-2 bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    value={editing1099Value}
                    onChange={(e) => setEditing1099Value(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit1099();
                      if (e.key === 'Escape') handleCancelEdit1099();
                    }}
                    placeholder="Enter contractor title..."
                    className={`flex-1 min-w-0 px-2 py-1 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    autoFocus
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSaveEdit1099(); }}
                      className="p-1 rounded hover:bg-green-200 dark:hover:bg-green-800 text-green-600 dark:text-green-400"
                      title="Save"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCancelEdit1099(); }}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500"
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}
              {/* Add card */}
              <div
                onClick={handleAdd1099Click}
                className={`p-4 rounded-lg border-2 border-dashed min-h-[80px] flex items-center justify-center gap-2 cursor-pointer transition-colors ${
                  darkMode 
                    ? 'border-gray-600 bg-gray-700/30 hover:bg-gray-700/50 text-gray-400 hover:text-gray-300' 
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              >
                <PlusCircle size={20} />
                <span className="font-medium">Add contractor</span>
              </div>
            </div>

            <div className="flex justify-between items-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-600">
              {/* Edit box - bottom left */}
              <div className="flex flex-col gap-2 min-w-0 flex-1">
                <div className="flex gap-2 flex-wrap items-center">
                  <button
                    onClick={() => setShowEditIcons1099(prev => !prev)}
                    className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${
                      showEditIcons1099
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <Pencil size={14} />
                    {showEditIcons1099 ? 'Done' : 'Edit'}
                  </button>
                  {editing1099Contractor && editing1099Contractor !== '__new__' && (
                    <>
                      <input
                        type="text"
                        value={editing1099Value}
                        onChange={(e) => setEditing1099Value(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit1099();
                          if (e.key === 'Escape') handleCancelEdit1099();
                        }}
                        className={`px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 flex-1 min-w-[120px] ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="New name"
                      />
                      <button
                        onClick={handleSaveEdit1099}
                        className="px-2 py-2 rounded-md hover:bg-green-100 dark:hover:bg-green-800/40 text-green-600 dark:text-green-400"
                        title="Save"
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={() => {
                          handleRemove1099Contractor(editing1099Contractor);
                          handleCancelEdit1099();
                        }}
                        className="px-2 py-2 rounded-md hover:bg-red-100 dark:hover:bg-red-800/40 text-red-600 dark:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                      <button
                        onClick={handleCancelEdit1099}
                        className="px-2 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500"
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* Save/Cancel - bottom right */}
              <div className="flex gap-3 shrink-0">
                <button
                  onClick={() => {
                    setIs1099ModalOpen(false);
                    handleCancelEdit1099();
                    setShowEditIcons1099(false);
                  }}
                  className={`px-4 py-2 rounded-md ${
                    darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave1099Contractors}
                  disabled={saving}
                  className={`px-4 py-2 rounded-md ${
                    saving 
                      ? 'bg-red-300 text-white cursor-not-allowed dark:bg-red-900/50' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Assignment Modal */}
      {isAssignmentModalOpen && selectedUnassignedMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md max-h-screen overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h2 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                Assign {selectedUnassignedMember.name}
              </h2>
              <button 
                onClick={() => setIsAssignmentModalOpen(false)}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Current Position: {selectedUnassignedMember.title}
                </h3>
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Select a suggested position to assign this team member to, or keep them in their current role.
                </p>
              </div>

              {analysisData && analysisData.suggestedPositionsWithThresholds && (
                <div>
                  <h4 className={`font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Suggested Positions
                  </h4>
                  <div className="space-y-2">
                    {analysisData.suggestedPositionsWithThresholds.map((position) => (
                      <label key={position.id} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="assignment"
                          value={position.id}
                          checked={memberAssignments[selectedUnassignedMember.id] === position.id}
                          onChange={() => handleAssignMember(selectedUnassignedMember.id, position.id)}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {position.title}
                          </span>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Hired at {formatCurrency(position.revenueThreshold)} revenue
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="assignment"
                    value=""
                    checked={!memberAssignments[selectedUnassignedMember.id]}
                    onChange={() => handleRemoveAssignment(selectedUnassignedMember.id)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Keep in current position ({selectedUnassignedMember.title})
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setIsAssignmentModalOpen(false)}
                  className={`px-4 py-2 rounded-md ${
                    darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Input Modal */}
      {isRevenueModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md max-h-screen overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h2 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                Enter Revenue Amount
              </h2>
              <button 
                onClick={() => setIsRevenueModalOpen(false)}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  To analyze your organizational chart, please enter your annual revenue amount.
                </p>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Annual Revenue *</label>
                <div className="flex items-center">
                  <span className={`mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                  <input
                    type="number"
                    value={revenueInput}
                    onChange={(e) => setRevenueInput(e.target.value)}
                    placeholder="Enter revenue amount..."
                    min="0"
                    step="1000"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleRevenueInputAnalysis();
                      }
                    }}
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setIsRevenueModalOpen(false)}
                  className={`px-4 py-2 rounded-md ${
                    darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevenueInputAnalysis}
                  disabled={!revenueInput || isNaN(parseFloat(revenueInput)) || parseFloat(revenueInput) <= 0}
                  className={`px-4 py-2 rounded-md ${
                    !revenueInput || isNaN(parseFloat(revenueInput)) || parseFloat(revenueInput) <= 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  Analyze
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hiring Outline Modal */}
      {isHiringOutlineOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col`}>
            <div className="relative mb-4">
              <h2 className={`text-xl font-bold tracking-tight text-center text-gray-900 dark:text-white`}>
                Hiring Outline
              </h2>
              <button 
                onClick={async () => {
                  setIsHiringOutlineOpen(false);
                  setIsHiringOutlineEditing(false);
                  const cid = await getCompanyIdForSave();
                  saveOrgChartData({
                    employees,
                    totalRevenue,
                    isChartFinalized,
                    finalizedEmployees,
                    proposedEmployees,
                    contractors1099: selected1099Contractors,
                    hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
                  }, cid).catch(console.error);
                }}
                className={`absolute right-0 top-0 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {(() => {
                  const futureMembers = employees.filter(e => e.memberStatus === 'future');
                  const displayOrder = hiringOrder.length > 0
                    ? [
                        ...hiringOrder.filter(id => futureMembers.some(m => m.id === id)),
                        ...futureMembers.filter(m => !hiringOrder.includes(m.id)).map(m => m.id)
                      ]
                    : Object.entries(getFutureMemberOrderMap(employees, null)).sort((a, b) => a[1] - b[1]).map(([id]) => id);
                  const moveUp = (index) => {
                    if (index <= 0) return;
                    const newOrder = [...displayOrder];
                    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                    setHiringOrder(newOrder);
                  };
                  const moveDown = (index) => {
                    if (index >= displayOrder.length - 1) return;
                    const newOrder = [...displayOrder];
                    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                    setHiringOrder(newOrder);
                  };
                  return displayOrder.map((empId, index) => {
                    const emp = employees.find(e => e.id === empId);
                    if (!emp) return null;
                    return (
                      <div
                        key={emp.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                          darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        {isHiringOutlineEditing ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => moveUp(index)}
                              disabled={index === 0}
                              className={`p-1 rounded ${index === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-500/30'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                              title="Move up"
                            >
                              <ChevronUp size={20} />
                            </button>
                            <button
                              onClick={() => moveDown(index)}
                              disabled={index === displayOrder.length - 1}
                              className={`p-1 rounded ${index === displayOrder.length - 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-500/30'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                              title="Move down"
                            >
                              <ChevronDown size={20} />
                            </button>
                            <span className={`w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-semibold`}>
                              {index + 1}
                            </span>
                          </div>
                        ) : (
                          <span className={`w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-semibold`}>
                            {index + 1}
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{emp.title}</div>
                          <div className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{emp.name}</div>
                        </div>
                        <div className="min-w-0 flex-1 max-w-[50%]">
                          {isHiringOutlineEditing ? (
                            <input
                              type="text"
                              placeholder="Hired At (e.g. Q2 2025, $2M revenue)"
                              value={hiredAtMap[emp.id] || ''}
                              onChange={(e) => setHiredAtMap(prev => ({ ...prev, [emp.id]: e.target.value }))}
                              className={`w-full min-w-0 px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                                darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                              }`}
                            />
                          ) : (
                            <div
                              className={`px-4 py-3 text-base break-words overflow-hidden ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                              title={hiredAtMap[emp.id] || undefined}
                            >
                              {hiredAtMap[emp.id] || '—'}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 flex justify-between items-center">
              <button
                onClick={() => setIsHiringOutlineEditing(prev => !prev)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  isHiringOutlineEditing 
                    ? darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                <Pencil size={16} />
                {isHiringOutlineEditing ? 'Done' : 'Edit'}
              </button>
              <button
                onClick={async () => {
                  setIsHiringOutlineOpen(false);
                  setIsHiringOutlineEditing(false);
                  const cid = await getCompanyIdForSave();
                  saveOrgChartData({
                    employees,
                    totalRevenue,
                    isChartFinalized,
                    finalizedEmployees,
                    proposedEmployees,
                    contractors1099: selected1099Contractors,
                    hiringOutline: { order: hiringOrder, hiredAt: hiredAtMap }
                  }, cid).catch(console.error);
                }}
                className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'}`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default OrgChart; 